import { kml as kmlToGeoJson } from "@tmcw/togeojson"

export function emptyFeatureCollection() {
  return { type: "FeatureCollection", features: [] }
}

export function cloneFeatureCollection(fc) {
  return JSON.parse(JSON.stringify(fc))
}

export function normalizeToFeatureCollection(input) {
  if (!input) return emptyFeatureCollection()
  if (input.type === "FeatureCollection") return input
  if (input.type === "Feature") return { type: "FeatureCollection", features: [input] }
  if (input.type && input.coordinates) {
    return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: input }] }
  }
  throw new Error("Not valid GeoJSON")
}

function parseExtendedDataProperties(placemark) {
  const dataNodes = placemark?.getElementsByTagName?.("Data") || []
  const props = {}

  for (let i = 0; i < dataNodes.length; i++) {
    const node = dataNodes[i]
    const name = node.getAttribute?.("name")
    if (!name) continue
    const valueNode = node.getElementsByTagName?.("value")?.[0]
    props[name] = valueNode?.textContent ?? ""
  }

  return props
}

function mergeExtendedDataIntoGeoJson(geojson, dom) {
  const placemarks = Array.from(dom.getElementsByTagName?.("Placemark") || [])
  const features = geojson?.features || []
  return {
    ...geojson,
    features: features.map((feature, index) => {
      const extended = parseExtendedDataProperties(placemarks[index])
      return {
        ...feature,
        properties: {
          ...feature?.properties,
          ...extended,
        },
      }
    }),
  }
}

export function normalizeImportedFeatureProperties(fc) {
  const out = cloneFeatureCollection(fc)
  out.features = (out.features || []).map((feature) => {
    const geometryType = feature?.geometry?.type
    const props = { ...feature?.properties }

    if (!props.iconUrl && props.icon) props.iconUrl = props.icon

    if (!props.color) {
      if (geometryType === "Point" || geometryType === "MultiPoint") {
        props.color = props["icon-color"] || "#000000"
      } else if (geometryType === "LineString" || geometryType === "MultiLineString") {
        props.color = props.stroke || "#0080ff"
      } else if (geometryType === "Polygon" || geometryType === "MultiPolygon") {
        props.color = props.fill || props.stroke || "#0080ff"
      }
    }

    if ((geometryType === "Polygon" || geometryType === "MultiPolygon") && props.opacity == null) {
      const fillOpacity = Number(props["fill-opacity"])
      props.opacity = Number.isFinite(fillOpacity) ? fillOpacity : 0.25
    }

    if (props.featureType != null) props.featureType = String(props.featureType)
    if (props.iconLabel != null) props.iconLabel = String(props.iconLabel)
    if (props.radiusUnit != null) props.radiusUnit = String(props.radiusUnit).toLowerCase()
    if (props.centerLat != null) props.centerLat = Number(props.centerLat)
    if (props.centerLng != null) props.centerLng = Number(props.centerLng)
    if (props.radius != null) props.radius = Number(props.radius)
    if (props.radiusMeters != null) props.radiusMeters = Number(props.radiusMeters)
    if (props.startBearing != null) props.startBearing = Number(props.startBearing)
    if (props.endBearing != null) props.endBearing = Number(props.endBearing)
    if (props.arcDirection != null) props.arcDirection = String(props.arcDirection)
    if (props.arcSweepDegrees != null) props.arcSweepDegrees = Number(props.arcSweepDegrees)

    return { ...feature, properties: props }
  })
  return out
}

export function ensureFeatureIds(fc) {
  const out = cloneFeatureCollection(fc)
  out.features = out.features.map((f) => {
    if (f?.id) return f
    return { ...f, id: globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : String(Date.now() + Math.random()) }
  })
  return out
}

function hasXmlParserError(dom) {
  if (!dom) return false
  if (dom.querySelector?.("parsererror")) return true
  return Number(dom.getElementsByTagName?.("parsererror")?.length || 0) > 0
}

export function parseImportedText(text, filename, options = {}) {
  const name = String(filename || "").toLowerCase()

  if (name.endsWith(".geojson") || name.endsWith(".json")) {
    const parsed = JSON.parse(String(text || ""))
    return ensureFeatureIds(normalizeImportedFeatureProperties(normalizeToFeatureCollection(parsed)))
  }

  if (name.endsWith(".kml")) {
    const DOMParserImpl = options.DOMParserImpl || globalThis.DOMParser
    if (!DOMParserImpl) throw new Error("DOMParser is unavailable in this environment.")

    const dom = new DOMParserImpl().parseFromString(String(text || ""), "text/xml")
    if (hasXmlParserError(dom)) throw new Error("Could not parse KML XML. Please check the file format.")

    const gj = mergeExtendedDataIntoGeoJson(kmlToGeoJson(dom), dom)
    return ensureFeatureIds(normalizeImportedFeatureProperties(normalizeToFeatureCollection(gj)))
  }

  throw new Error(`Unsupported file type: ${filename}`)
}

function escapeXml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;")
}

function hexToKmlColor(hex, alpha = 1) {
  const clean = String(hex || "#000000").trim().replace(/^#/, "")
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean
  const valid = /^[0-9a-fA-F]{6}$/.test(full) ? full.toLowerCase() : "000000"
  const rr = valid.slice(0, 2)
  const gg = valid.slice(2, 4)
  const bb = valid.slice(4, 6)
  const aa = Math.round(Math.min(1, Math.max(0, Number(alpha ?? 1))) * 255)
    .toString(16)
    .padStart(2, "0")
  return `${aa}${bb}${gg}${rr}`
}

function coordsToKml(coordinates) {
  if (!Array.isArray(coordinates)) return ""
  return coordinates
    .map((coord) => {
      if (!Array.isArray(coord) || coord.length < 2) return ""
      const [lng, lat, alt] = coord
      return Number.isFinite(Number(alt)) ? `${lng},${lat},${alt}` : `${lng},${lat},0`
    })
    .filter(Boolean)
    .join(" ")
}

function geometryToKml(geometry) {
  if (!geometry?.type || !geometry?.coordinates) return ""
  if (geometry.type === "Point") {
    return `<Point><coordinates>${coordsToKml([geometry.coordinates])}</coordinates></Point>`
  }
  if (geometry.type === "MultiPoint") {
    const points = (geometry.coordinates || []).map((c) => `<Point><coordinates>${coordsToKml([c])}</coordinates></Point>`).join("")
    return `<MultiGeometry>${points}</MultiGeometry>`
  }
  if (geometry.type === "LineString") {
    return `<LineString><coordinates>${coordsToKml(geometry.coordinates)}</coordinates></LineString>`
  }
  if (geometry.type === "MultiLineString") {
    const lines = (geometry.coordinates || []).map((line) => `<LineString><coordinates>${coordsToKml(line)}</coordinates></LineString>`).join("")
    return `<MultiGeometry>${lines}</MultiGeometry>`
  }
  if (geometry.type === "Polygon") {
    const rings = geometry.coordinates || []
    const outer = rings[0] || []
    const outerKml = `<outerBoundaryIs><LinearRing><coordinates>${coordsToKml(outer)}</coordinates></LinearRing></outerBoundaryIs>`
    const innerKml = rings.slice(1).map((hole) =>
      `<innerBoundaryIs><LinearRing><coordinates>${coordsToKml(hole)}</coordinates></LinearRing></innerBoundaryIs>`
    ).join("")
    return `<Polygon>${outerKml}${innerKml}</Polygon>`
  }
  if (geometry.type === "MultiPolygon") {
    const polys = (geometry.coordinates || []).map((poly) => {
      const outer = poly?.[0] || []
      const outerKml = `<outerBoundaryIs><LinearRing><coordinates>${coordsToKml(outer)}</coordinates></LinearRing></outerBoundaryIs>`
      const innerKml = (poly || []).slice(1).map((hole) =>
        `<innerBoundaryIs><LinearRing><coordinates>${coordsToKml(hole)}</coordinates></LinearRing></innerBoundaryIs>`
      ).join("")
      return `<Polygon>${outerKml}${innerKml}</Polygon>`
    }).join("")
    return `<MultiGeometry>${polys}</MultiGeometry>`
  }
  return ""
}

export function buildKmlStylesAndPlacemarks(features) {
  const styles = []
  const placemarks = []

  features.forEach((feature, idx) => {
    const geometry = feature?.geometry
    const kmlGeometry = geometryToKml(geometry)
    if (!kmlGeometry) return

    const props = feature?.properties || {}
    const name = escapeXml(props.name || `Feature ${idx + 1}`)
    const description = escapeXml(props.description || "")
    const color = props.color || "#000000"
    const opacity = Math.min(1, Math.max(0, Number(props.opacity ?? 0.35)))
    const styleId = `style-${idx}`

    let styleBody = ""
    if (geometry.type === "Point" || geometry.type === "MultiPoint") {
      const iconHref = escapeXml(props.iconUrl || "http://maps.google.com/mapfiles/kml/pushpin/red-pushpin.png")
      styleBody = `<IconStyle><color>${hexToKmlColor(color, 1)}</color><Icon><href>${iconHref}</href></Icon></IconStyle>`
    } else if (geometry.type === "LineString" || geometry.type === "MultiLineString") {
      styleBody = `<LineStyle><color>${hexToKmlColor(color, 1)}</color><width>3</width></LineStyle>`
    } else if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
      styleBody = `<LineStyle><color>${hexToKmlColor(color, 1)}</color><width>3</width></LineStyle><PolyStyle><color>${hexToKmlColor(color, opacity)}</color></PolyStyle>`
    }

    styles.push(`<Style id="${styleId}">${styleBody}</Style>`)

    const extended = []
    if (props.featureType) extended.push(`<Data name="featureType"><value>${escapeXml(props.featureType)}</value></Data>`)
    if (props.iconLabel) extended.push(`<Data name="iconLabel"><value>${escapeXml(props.iconLabel)}</value></Data>`)
    if (props.centerLat != null) extended.push(`<Data name="centerLat"><value>${escapeXml(props.centerLat)}</value></Data>`)
    if (props.centerLng != null) extended.push(`<Data name="centerLng"><value>${escapeXml(props.centerLng)}</value></Data>`)
    if (props.radius != null) extended.push(`<Data name="radius"><value>${escapeXml(props.radius)}</value></Data>`)
    if (props.radiusUnit) extended.push(`<Data name="radiusUnit"><value>${escapeXml(props.radiusUnit)}</value></Data>`)
    if (props.radiusMeters != null) extended.push(`<Data name="radiusMeters"><value>${escapeXml(props.radiusMeters)}</value></Data>`)
    if (props.startBearing != null) extended.push(`<Data name="startBearing"><value>${escapeXml(props.startBearing)}</value></Data>`)
    if (props.endBearing != null) extended.push(`<Data name="endBearing"><value>${escapeXml(props.endBearing)}</value></Data>`)
    if (props.arcDirection) extended.push(`<Data name="arcDirection"><value>${escapeXml(props.arcDirection)}</value></Data>`)
    if (props.arcSweepDegrees != null) extended.push(`<Data name="arcSweepDegrees"><value>${escapeXml(props.arcSweepDegrees)}</value></Data>`)
    const extendedXml = extended.length ? `<ExtendedData>${extended.join("")}</ExtendedData>` : ""

    placemarks.push(`<Placemark><name>${name}</name><description>${description}</description><styleUrl>#${styleId}</styleUrl>${extendedXml}${kmlGeometry}</Placemark>`)
  })

  return { styles, placemarks }
}

export function featureCollectionToKml(fc) {
  const { styles, placemarks } = buildKmlStylesAndPlacemarks(fc?.features || [])
  return `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n${styles.join("\n")}\n${placemarks.join("\n")}\n</Document>\n</kml>`
}
